import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Upload, CheckCircle2, Loader2, Smartphone, Zap, MessageCircle } from "lucide-react";
import { api, egp, errMsg, waLink } from "@/lib/api";
import { useSettings } from "@/lib/providers";
import { useSeo } from "@/lib/seo";
import { FullLoader } from "@/components/site/Layout";

const MethodCard = ({ id, active, onClick, icon: Icon, title, number, extra }) => (
  <button type="button" onClick={onClick} data-testid={`pay-method-${id}`}
    className={`text-right w-full rounded-2xl p-5 border-2 transition-colors ${active ? "border-[var(--kh-violet)] bg-purple-50" : "border-line bg-white hover:border-[#C9A3EE]"}`}>
    <span className="flex items-center gap-3 mb-3"><Icon className={active ? "text-brand" : "text-soft"} /><span className="font-heading font-bold text-lg text-ink">{title}</span></span>
    <span className="flex items-center justify-between gap-2">
      <span className="font-display text-2xl tracking-wider text-brand" dir="ltr" data-testid={`pay-number-${id}`}>{number}</span>
      <span role="button" tabIndex={0} data-testid={`copy-number-${id}`} onClick={(e) => { e.stopPropagation(); navigator.clipboard?.writeText(number); toast.success("تم نسخ الرقم"); }}
        className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-purple-100 text-brand hover:bg-purple-200"><Copy size={13} />نسخ</span>
    </span>
    {extra && <span className="block mt-2 text-xs text-soft" dir="ltr">{extra}</span>}
  </button>
);

const Success = ({ s }) => (
  <div className="max-w-xl mx-auto text-center py-24 px-4 space-y-5" data-testid="checkout-success">
    <CheckCircle2 className="mx-auto text-brand" size={64} />
    <h1 className="font-display text-4xl text-ink">وصلنا طلبك!</h1>
    <p className="text-soft">طلب الدفع دلوقتي قيد المراجعة. أول ما نتأكد من التحويل هيتفعل الكورس في حسابك.</p>
    <div className="flex flex-wrap justify-center gap-3">
      <Link to="/dashboard" data-testid="checkout-go-dashboard" className="btn-pop btn-primary px-6 py-3 rounded-full font-bold">لوحة الطالب</Link>
      {s.whatsapp_number && <a href={waLink(s.whatsapp_number)} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-6 py-3 rounded-full border border-line text-brand"><MessageCircle size={18} />كلمنا واتساب</a>}
    </div>
  </div>
);

export default function Checkout() {
  useSeo("إتمام الاشتراك");
  const { slug } = useParams();
  const nav = useNavigate();
  const { settings: s } = useSettings();
  const [c, setC] = useState(null);
  const [method, setMethod] = useState(null);
  const [phone, setPhone] = useState("");
  const [ref, setRef] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get(`/courses/${slug}`).then((r) => {
      if (r.data.enrolled) nav(`/learn/${r.data.id}`, { replace: true });
      setC(r.data);
    }).catch(() => nav("/courses"));
  }, [slug, nav]);
  useEffect(() => {
    if (!method && s.vodafone_number !== undefined) setMethod(s.vodafone_enabled ? "vodafone_cash" : s.instapay_enabled ? "instapay" : null);
  }, [s, method]);

  const pickFile = (f) => {
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp", "application/pdf"].includes(f.type)) return toast.error("الملف لازم يكون صورة (JPG/PNG/WEBP) أو PDF");
    if (f.size > 8 * 1024 * 1024) return toast.error("حجم الملف أكبر من 8 ميجابايت");
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("ارفع صورة إيصال التحويل");
    const fd = new FormData();
    fd.append("course_id", c.id);
    fd.append("method", method);
    fd.append("sender_phone", phone);
    fd.append("reference", ref);
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post("/payments", fd);
      setDone(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (!c) return <FullLoader />;
  if (done) return <Success s={s} />;
  if (c.pending_payment) return (
    <div className="text-center py-28 space-y-4" data-testid="checkout-already-pending">
      <p className="text-ink text-lg">عندك طلب دفع قيد المراجعة لهذا الكورس.</p>
      <Link to="/dashboard" className="text-brand underline">تابع حالة الطلب من لوحة الطالب</Link>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14" data-testid="checkout-page">
      <h1 className="font-display text-4xl sm:text-5xl text-ink mb-2">إتمام الاشتراك</h1>
      <p className="text-soft mb-10">{c.title}</p>
      <form onSubmit={submit} className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 space-y-8">
          <section className="space-y-4">
            <h2 className="font-heading text-xl font-bold text-ink">1. اختار طريقة الدفع وحوّل المبلغ</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {s.vodafone_enabled && <MethodCard id="vodafone_cash" icon={Smartphone} title="فودافون كاش" number={s.vodafone_number} active={method === "vodafone_cash"} onClick={() => setMethod("vodafone_cash")} />}
              {s.instapay_enabled && <MethodCard id="instapay" icon={Zap} title="إنستاباي" number={s.instapay_number} extra={s.instapay_handle} active={method === "instapay"} onClick={() => setMethod("instapay")} />}
            </div>
            {!s.vodafone_enabled && !s.instapay_enabled && <p className="text-brand">طرق الدفع غير متاحة حالياً، تواصل معنا على واتساب.</p>}
            <p className="text-sm text-soft leading-relaxed bg-soft rounded-2xl p-4 border border-line whitespace-pre-line" data-testid="payment-instructions">{s.payment_instructions}</p>
          </section>
          <section className="space-y-4">
            <h2 className="font-heading text-xl font-bold text-ink">2. ارفع إثبات الدفع</h2>
            <label className="block space-y-2">
              <span className="text-sm text-soft">الرقم اللي حوّلت منه *</span>
              <input required value={phone} onChange={(e) => setPhone(e.target.value)} dir="ltr" placeholder="01xxxxxxxxx" data-testid="checkout-sender-phone" className="kh-input text-right" />
            </label>
            <label className="block space-y-2">
              <span className="text-sm text-soft">رقم العملية (اختياري)</span>
              <input value={ref} onChange={(e) => setRef(e.target.value)} data-testid="checkout-reference" className="kh-input" />
            </label>
            <div onClick={() => fileRef.current?.click()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pickFile(e.dataTransfer.files[0]); }}
              data-testid="checkout-receipt-dropzone"
              className="cursor-pointer rounded-2xl border-2 border-dashed border-[#C9A3EE] bg-soft hover:border-[var(--kh-violet)] p-8 text-center transition-colors">
              <Upload className="mx-auto mb-2 text-brand" />
              {file ? <p className="text-ink text-sm" data-testid="checkout-file-name">{file.name}</p> : <p className="text-sm text-soft">اسحب صورة الإيصال هنا أو اضغط للاختيار<br /><span className="text-xs text-soft/70">JPG, PNG, WEBP, PDF — حتى 8 ميجا</span></p>}
              <input ref={fileRef} type="file" hidden accept="image/jpeg,image/png,image/webp,application/pdf" onChange={(e) => pickFile(e.target.files[0])} data-testid="checkout-receipt-input" />
            </div>
          </section>
        </div>
        <aside className="lg:col-span-5">
          <div className="lg:sticky lg:top-24 rounded-3xl bg-white sticker p-7 space-y-5">
            <h3 className="font-heading font-bold text-lg text-ink">ملخص الطلب</h3>
            <div className="flex justify-between text-sm text-soft"><span>{c.title}</span></div>
            <div className="flex justify-between items-end border-t border-line pt-4">
              <span className="text-soft">الإجمالي</span>
              <span className="font-display text-3xl text-brand" data-testid="checkout-total">{egp(c.price)}</span>
            </div>
            <button disabled={busy || !method} data-testid="checkout-submit-btn" className="btn-pop btn-primary w-full py-4 rounded-full font-bold disabled:opacity-60 flex items-center justify-center gap-2">
              {busy && <Loader2 className="animate-spin" size={18} />}إرسال طلب التفعيل
            </button>
            <p className="text-xs text-soft">بإرسال الطلب أنت موافق على <Link to="/terms" className="underline text-brand">الشروط والأحكام</Link>.</p>
          </div>
        </aside>
      </form>
    </div>
  );
}
